<script setup lang="ts">
    import { computed, onMounted, ref, watch, watchEffect } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import ModulhandbuchFilter from '@/js/components/ModulHandBuch/ModulhandbuchFilter.vue'
    import ModulHandbuchCollapseView from '@/js/components/ModulHandBuch/ModulHandbuchCollapseView.vue'
    import ModulHandbuchSidenavView from '@/js/components/ModulHandBuch/ModulHandbuchSidenavView.vue'
    import { useModuleAccess } from '@/js/components/Common/useModuleAccess'
    import { useElementSize, useStorage } from '@vueuse/core'
    import {
        useLayoutStore,
        useModulHandbuchStore,
        useFachbereichStore,
        useStudiengangStore,
        useUserStore,
        useTooltipStore
    } from '@/js/stores'
    import { toSemesterSlug } from '@/js/composables/useSemester'

    const route = useRoute()
    const router = useRouter()

    const isLoading = ref(true)
    const selectedModulHandbuchId = ref<string | null>(null)
    const filterModulesForUser = ref('')
    const sideNavMode = useStorage('lernleitstand-sidenav-mode', true)
    const layoutStore = useLayoutStore()

    const filterRowRef = ref<HTMLElement | null>(null)
    const { height } = useElementSize(filterRowRef)

    const { updateStudiengangAccess, updateFachbereichAccess } = useModuleAccess()

    const handleFachbereichSelected = (fachbereichIri: string) => {
        const fachbereichId = fachbereichIri.split('/').pop()
        updateFachbereichAccess(fachbereichId, store.getters['security/currentUser'])
    }
    const handleStudiengangSelected = (studiengangId) => {
        updateStudiengangAccess(studiengangId, store.getters['security/currentUser'])
    }

    /**
     * Wenn ein Modulhandbuch über den Filter ausgewählt wird, aktualisieren wir
     * die URL auf /:kuerzel/:semester, damit der Link teilbar ist.
     */
    const handleModulhandbuchSelected = (modulhandbuchId: string) => {
        selectedModulHandbuchId.value = modulhandbuchId

        if (!modulhandbuchId || modulhandbuchId === 'default') return

        const mhb = modulhandbuchStore.getModulHandbuchById(modulhandbuchId)
        if (!mhb) return

        const studiengang = studiengangStore.getStudiengangById(mhb.studiengangId)
        if (!studiengang?.kurzName) return

        const semesterSlug = toSemesterSlug(mhb.semester ?? '')
        // Nur pushen wenn wir noch nicht auf der richtigen URL sind
        if (
            route.params.kuerzel !== studiengang.kurzName ||
            route.params.semester !== semesterSlug
        ) {
            router.push({
                name: 'ModulhandbuchBySemester',
                params: { kuerzel: studiengang.kurzName, semester: semesterSlug }
            })
        }
    }

    const handleUserFilterSelected = (selection) => {
        if ('alle' === selection) {
            filterModulesForUser.value = ''
        }
        if ('nurEigene' === selection) {
            const user = store.getters['security/currentUser']
            filterModulesForUser.value = user['userId']
        }
    }

    watchEffect(() => {
        layoutStore.filterRowHeight = height.value
    })

    const modulhandbuchStore = useModulHandbuchStore()
    const userStore = useUserStore()
    const tooltipStore = useTooltipStore()
    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()

    const selectedModulHandbuch = computed(() =>
        selectedModulHandbuchId.value
            ? modulhandbuchStore.getModulHandbuchById(selectedModulHandbuchId.value)
            : null
    )

    const studiengaenge = computed(() => studiengangStore.studiengaenge)
    const fachbereiche = computed(() => fachbereichStore.getFachbereiche)
    const modulhandbuecher = computed(() => modulhandbuchStore.getModulhandbuecher)

    /**
     * Wenn die Route URL-Parameter (:kuerzel/:semester) enthält, das passende
     * Modulhandbuch automatisch vorselektieren.
     * Läuft nach dem initialen Datenladen und bei Route-Wechseln.
     */
    function applyUrlParams() {
        const { kuerzel, semester } = route.params
        if (!kuerzel || !semester) return

        const studiengang = studiengangStore.getStudiengangByKurzName(String(kuerzel))
        if (!studiengang?.studiengangId) return

        const mhb = modulhandbuchStore.getModulhandbuchByStudiengangAndSemesterSlug(
            studiengang.studiengangId,
            String(semester)
        )
        if (!mhb) return

        selectedModulHandbuchId.value = mhb.modulHandbuchId
    }

    onMounted(async () => {
        isLoading.value = true
        await Promise.all([
            store.dispatch('user/findAll'),
            studiengangStore.fetchStudiengaenge(),
            modulhandbuchStore.fetchModulHandbuecher(),
            fachbereichStore.fetchFachbereiche(),
            userStore.fetchUsers(),
            tooltipStore.fetchTooltips()
        ])
        isLoading.value = false
        applyUrlParams()
    })

    watch(() => route.params, applyUrlParams)
</script>

<template>
    <v-row v-if="!isLoading" ref="filterRowRef" class="border-b border-primary align-center">
        <v-col cols="12" lg="10">
            <ModulhandbuchFilter
                :studiengaenge="studiengaenge"
                :fachbereiche="fachbereiche"
                :modulhandbuecher="modulhandbuecher"
                @fachbereich-selected="handleFachbereichSelected"
                @studiengang-selected="handleStudiengangSelected"
                @modulhandbuch-selected="handleModulhandbuchSelected"
                @user-filter-selected="handleUserFilterSelected"
            />
        </v-col>
        <v-col cols="12" lg="2" class="d-flex justify-end align-center">
            <v-switch
                v-model="sideNavMode"
                size="lg"
                hide-details
                color="primary"
                density="compact"
                label="SideNav"
                class="pr-5 mb-1"
                data-test="side-nav-toggle"
            />
        </v-col>
    </v-row>
    <v-row v-if="isLoading || !selectedModulHandbuch" class="mt-4 flex-nowrap">
        <v-col cols="2" class="border-e border-primary pr-4">
            <v-skeleton-loader type="button" class="mb-2" />
            <v-skeleton-loader type="button" class="mb-4" />
            <v-skeleton-loader v-for="i in 8" :key="i" type="list-item" />
        </v-col>
        <v-col cols="10" class="pl-4">
            <v-skeleton-loader type="heading" class="mb-4" />
            <v-skeleton-loader type="table-heading" class="mb-1" />
            <v-skeleton-loader v-for="i in 6" :key="i" type="table-tfoot" />
        </v-col>
    </v-row>

    <v-row v-if="selectedModulHandbuch">
        <v-col v-if="!sideNavMode" offset-md="1" md="10" class="mt-3 pt-0" align-self="center">
            <ModulHandbuchCollapseView
                :modul-handbuch="selectedModulHandbuch"
                :filter-modules-for-user="filterModulesForUser"
            />
        </v-col>
        <v-col v-if="sideNavMode" cols="12" class="mt-0 pt-0">
            <ModulHandbuchSidenavView
                :modul-handbuch="selectedModulHandbuch"
                :filter-modules-for-user="filterModulesForUser"
            />
        </v-col>
    </v-row>
</template>

<script></script>

<style scoped></style>
